import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import {
  CreateCardRequest,
  CreateCardResponse,
  EditCardRequest,
  UniqueCheckResponse,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useUserUidNumber } from "../auth/useUserUidNumber.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { NotUniqueError } from "./errors.ts";
import { useCreateCardProjectCache } from "./useCreateCardProjectCache.ts";
import { useEditCardEditCache } from "./useEditCardEditCache.ts";
import { useInvalidateCardErrorsCache } from "./useInvalidateCardErrorsCache.ts";

export const useCreateCard = () => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const userUidNumber = useUserUidNumber();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const createCardInProjectCache = useCreateCardProjectCache();
  const createCardInEditCache = useEditCardEditCache();
  const invalidateCardErrorsCache = useInvalidateCardErrorsCache();
  return useMutation({
    mutationFn: async (newCard: CreateCardRequest): Promise<Response> => {
      const token = await getToken();
      if (newCard.recipient.email) {
        const uniqueCheckResponse = await fetch(
          `/api/projects/${encodeURIComponent(newCard.projectId)}/cardUnique?email=${encodeURIComponent(newCard.recipient.email)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!uniqueCheckResponse.ok) {
          throw new Error("Checking the card email failed");
        }

        const { isUnique } = UniqueCheckResponse.parse(
          await uniqueCheckResponse.json(),
        );

        if (!isUnique) {
          throw new NotUniqueError();
        }
      }

      const response = await fetch(`/api/projects/${newCard.projectId}/cards`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newCard),
      });

      if (!response.ok) {
        throw new Error("Creating the card failed");
      }

      return response;
    },
    mutationKey: ["createCard"],
    onSuccess: async (response, newCard: CreateCardRequest) => {
      const parseResult = CreateCardResponse.safeParse(await response.json());
      if (!parseResult.success) {
        throw new Error("Invalid card ID in response");
      }
      const newCardItem: EditCardRequest = {
        id: parseResult.data.id,
        ...newCard,
      };
      createCardInProjectCache(newCardItem, newCard.projectId, userUidNumber);
      createCardInEditCache(newCardItem);
      invalidateCardErrorsCache();
      displaySuccess(translator("changesSaved"));
    },
    onError: (e) => {
      if (e instanceof NotUniqueError) {
        displayError(translator("errorCardEmailNotUnique"));
      } else {
        displayError(translator("errorSavingChanges"));
      }
    },
  });
};
